import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";

import { supabase } from "../supabaseClient";
import {
  loadAdjustmentsByManager,
  adjustedScheduleDayjs,
  adjustedScheduleInEastern,
} from "../utils/crewTimeAdjustment";

// Calendar-day bounds (local time): trips are bucketed by their actual date,
// so a 00:05 trip on June 1 is a June trip — matching the legacy system, the
// Reports download, and airline billing. Local-day instants keep the window
// aligned with how ScheduleDate displays and stop the previous day leaking in
// via the TIMESTAMPTZ/UTC mismatch. See src/components/Reports/index.js.
const reportDayStart = (date) => dayjs(date).startOf("day").toISOString();
const reportDayEndExclusive = (date) =>
  dayjs(date).startOf("day").add(1, "day").toISOString();

const createAirline = async (data) => {
  let maxID = 1;
  const { data: _data, error: _err } = await supabase
    .from("Airlines")
    .select("AirlineID")
    .order("AirlineID", { ascending: false })
    .limit(1);

  if (_err) {
  } else {
    maxID = _data[0]?.AirlineID || 1;
  }
  const { error } = await supabase.from("Airlines").insert([
    {
      AirlineID: maxID + 1,
      CompanyID: data.CompanyID,
      Code: data.Code,
      Description: data.Description,
      AirlineUser: data.AirlineUser,
      HotelUser: data.HotelUser,
      HotelPass: data.HotelPass,
      CrewUser: data.CrewUser,
      CrewPass: data.CrewPass,
      PortalPassReset: data.PortalPassReset,
      Active: data.Active,
      AirlineLetterCode: data.AirlineLetterCode,
      AirlinePass: data.AirlinePass,
    },
  ]);
  if (error) throw new Error(error.toString());
  return;
};

const updateAirline = async (data) => {
  const { error } = await supabase
    .from("Airlines")
    .update({
      CompanyID: data.CompanyID,
      Code: data.Code,
      Description: data.Description,
      AirlineUser: data.AirlineUser,
      HotelUser: data.HotelUser,
      HotelPass: data.HotelPass,
      CrewUser: data.CrewUser,
      CrewPass: data.CrewPass,
      PortalPassReset: data.PortalPassReset,
      Active: data.Active,
      AirlineLetterCode: data.AirlineLetterCode,
      AirlinePass: data.AirlinePass,
    })
    .eq("AirlineID", data.ID);
  if (error) throw new Error(error.toString());
  return;
};

const createTrip = async (data) => {
  console.log("createTrip raw data:", data);
  // One-off trips skip the manager's time adjustment on display, regardless
  // of airline. For JetBlue (B6), the entered time IS the pickup time, so
  // we also stamp it into PickupDateTime — that's what the display layer
  // reads for B6. ScheduleDate is always set so date filters and reports
  // keep treating the row at the right point on the timeline.
  const isB6 = (data.airlineCode || "").toUpperCase() === "B6";
  // ADHOC marker: every TripAdd row is a one-off and gets billed separately
  // from the imported monthly schedule. Suffix the flight number with
  // "-ADHOC" so reports and exports can pick it out at a glance. Skip the
  // append when the user already typed the suffix in.
  const rawFlightNo = (data.flight_no || "").toString().trim();
  const flightNo = rawFlightNo && !/-ADHOC$/i.test(rawFlightNo)
    ? `${rawFlightNo}-ADHOC`
    : rawFlightNo;
  const insertFields = {
    CompanyID: parseInt(data.CompanyID),
    ScheduleStatusID: 1,
    ScheduleTypeID: 1,
    TransportationTypeID: parseInt(data.option),
    AirlineID: parseInt(data.airline),
    DriverID: parseInt(data.driver),
    VehicleID: parseInt(data.vehicle),
    ScheduleDate: data.date,
    PickupDateTime: isB6 ? data.date : null,
    IsOneOff: true,
    FlightNo: flightNo,
    RateOverride: parseFloat(data.rate),
    CreatedDate: dayjs().toDate(),
  };
  if (data.managerId) insertFields.ManagerID = parseInt(data.managerId);
  console.log("createTrip insertFields:", insertFields);

  const { data: schedule, error: scheduleError } = await supabase
    .from("Schedule")
    .insert(insertFields)
    .select();

  console.log("Schedule insert result:", { schedule, scheduleError });
  if (scheduleError || !schedule || schedule.length == 0)
    throw new Error(scheduleError?.message || "Error when creating a schedule");
  const { error: pickupError } = await supabase.from("SchedulePickups").insert({
    ScheduleID: schedule[0].ScheduleID,
    LocationID: parseInt(data.pu),
    Passengers: parseInt(data.passengers),
  });
  if (pickupError) throw new Error(pickupError.message || "Error when creating a pickUp");
  const { error: dropOffError } = await supabase
    .from("ScheduleDropoffs")
    .insert({
      ScheduleID: schedule[0].ScheduleID,
      LocationID: parseInt(data.do),
    })
    .select();

  if (dropOffError) throw new Error(dropOffError.message || "Error when creating a dropOff");
  // Return the newly-created ScheduleID so callers (Add Trip) can highlight
  // fresh rows on the schedule list when the user returns to it.
  return { ScheduleID: schedule[0].ScheduleID };
};

const updateTrip = async (data) => {
  // For JetBlue we keep PickupDateTime in sync with the edited date so the
  // B6 display continues to read the user-entered time. For non-B6 we don't
  // touch PickupDateTime — preserving whatever the importer stored for
  // imported rows, and leaving NULL alone for non-B6 one-offs. IsOneOff is
  // never overwritten by update — it stays whatever it was when the row
  // was first created.
  const isB6 = (data.airlineCode || "").toUpperCase() === "B6";
  const updateFields = {
    CompanyID: parseInt(data.CompanyID),
    ScheduleStatusID: 1,
    ScheduleTypeID: 1,
    TransportationTypeID: parseInt(data.option),
    AirlineID: parseInt(data.airline),
    DriverID: parseInt(data.driver),
    VehicleID: parseInt(data.vehicle),
    ScheduleDate: data.date,
    FlightNo: data.flight_no,
    RateOverride: parseFloat(data.rate),
    CreatedDate: dayjs().toDate(),
  };
  if (isB6) updateFields.PickupDateTime = data.date;
  if (data.managerId) updateFields.ManagerID = parseInt(data.managerId);

  const { error: scheduleError } = await supabase
    .from("Schedule")
    .update(updateFields)
    .eq("ScheduleID", data.scheduleID);

  if (scheduleError) throw new Error(scheduleError.message || "Error when updating a schedule");
  const { error: pickupError } = await supabase
    .from("SchedulePickups")
    .update({
      LocationID: parseInt(data.pu),
      Passengers: parseInt(data.passengers),
    })
    .eq("ScheduleID", data.scheduleID);
  if (pickupError) throw new Error(pickupError.message || "Error when updating a pickUp");
  const { error: dropOffError } = await supabase
    .from("ScheduleDropoffs")
    .update({
      LocationID: parseInt(data.do),
    })
    .eq("ScheduleID", data.scheduleID);

  if (dropOffError) throw new Error(dropOffError.message || "Error when updating a dropOff");
  return;
};

const createManager = async (data) => {
  // Support both single locationId and array of locationIds
  const locationIds = data.locationIds || (data.locationId ? [data.locationId] : []);

  const { data: user, error: userError } = await supabase
    .from("Users")
    .insert({
      Email: data.email,
      Password: data.password,
      RoleID: 2,
      Active: 1,
      LocationID: locationIds[0] || null,
    })
    .select();

  if (userError || user.length == 0)
    throw new Error("Error when creating a manager");

  // Insert into UserLocations junction table
  if (locationIds.length > 0) {
    const userLocationRows = locationIds.map((locId) => ({
      UserID: user[0].UserID,
      LocationID: locId,
    }));
    const { error: ulError } = await supabase
      .from("UserLocations")
      .insert(userLocationRows);
    if (ulError) console.error("Error inserting UserLocations:", ulError);
  }

  const { data: managerAuth, error: _managerAuthErr } = await supabase
    .from("manager_auth")
    .insert({
      email: data.email,
      password_hash: data.password,
    })
    .select();
  if (_managerAuthErr || managerAuth.length == 0)
    throw new Error("Error when creating a manager auth");
  const { error: _managerProfileErr } = await supabase
    .from("manager_profiles")
    .insert({
      auth_id: managerAuth[0].id,
      first_name: data.firstname,
      last_name: data.lastname,
    });
  if (_managerProfileErr)
    throw new Error("Error when creating a manager profile");

  const { error: _managerErr } = await supabase.from("Managers").insert({
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    auth_id: managerAuth[0].id,
    UserID: user[0].UserID,
  });

  if (_managerErr) throw new Error("Error when creating a manager");
  return;
};

const updateManager = async (data) => {
  // Support both single locationId and array of locationIds
  const locationIds = data.locationIds || (data.locationId ? [data.locationId] : []);

  const { data: user, error: UserErr } = await supabase
    .from("Users")
    .select(
      `
      Email
    `
    )
    .eq("UserID", data.UserID);
  if (UserErr) throw new Error("Error when updating a manager");
  const updateUserFields = {
    Email: data.email,
    RoleID: 2,
    Active: 1,
    LocationID: locationIds[0] || null,
  };
  if (data.password) {
    updateUserFields.Password = data.password;
  }

  const { err: updateUserErr } = await supabase
    .from("Users")
    .update(updateUserFields)
    .eq("UserID", data.UserID);

  if (updateUserErr) throw new Error("Error when updating a manager");

  // Replace UserLocations: delete old, insert new
  await supabase
    .from("UserLocations")
    .delete()
    .eq("UserID", data.UserID);

  if (locationIds.length > 0) {
    const userLocationRows = locationIds.map((locId) => ({
      UserID: parseInt(data.UserID),
      LocationID: locId,
    }));
    const { error: ulError } = await supabase
      .from("UserLocations")
      .insert(userLocationRows);
    if (ulError) console.error("Error updating UserLocations:", ulError);
  }

  const updateManagerAuthFields = {
    email: data.email,
  };
  if (data.password) {
    updateManagerAuthFields.password_hash = data.password;
  }
  const { data: managerAuth, err: updateManagerAuthErr } = await supabase
    .from("manager_auth")
    .update(updateManagerAuthFields)
    .eq("email", user[0].Email)
    .select();
  if (updateManagerAuthErr || managerAuth.length == 0)
    throw new Error("Error when updating a manager auth");
  const { error: _managerProfileErr } = await supabase
    .from("manager_profiles")
    .update({
      first_name: data.firstname,
      last_name: data.lastname,
    })
    .eq("auth_id", managerAuth[0].id);
  if (_managerProfileErr)
    throw new Error("Error when updating a manager profile");

  const { error: _managerErr } = await supabase
    .from("Managers")
    .update({
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
    })
    .eq("UserID", data.UserID);

  if (_managerErr) throw new Error("Error when creating a manager");
  return;
};

const createDriverNote = async (data) => {
  const { data: driverNote, error: driverNoteError } = await supabase
    .from("Driver_Notes")
    .insert({
      DriverID: data.driverID,
      Memo: data.memo,
    })
    .select();
  if (driverNoteError || driverNote.length == 0)
    throw new Error("Error when creating a DriverNote");
  return;
};

const updateDriverNote = async (data) => {
  const { error: _err } = await supabase
    .from("Driver_Notes")
    .update({
      DriverID: data.driverID,
      Memo: data.memo,
    })
    .eq("ID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const createHQCompany = async (data) => {
  const { data: HQCompany, error: HQCompanyError } = await supabase
    .from("HQCompanies")
    .insert({
      HQID: data.HQID,
      CompanyID: data.CompanyID,
    })
    .select();
  if (HQCompanyError || HQCompany.length == 0)
    throw new Error("Error when creating a HQCompany");
  return;
};

const updateHQCompany = async (data) => {
  const { error: _err } = await supabase
    .from("HQCompanies")
    .update({
      HQID: data.HQID,
      CompanyID: data.CompanyID,
    })
    .eq("HQCompanyID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const createHQs = async (data) => {
  const { data: HQ, error: HQError } = await supabase
    .from("HQs")
    .insert({
      Code: data.Code,
      Description: data.Description,
      HQUser: data.HQUser,
      HQPass: data.HQPass,
      Active: data.Active === true ? 1 : 0,
    })
    .select();
  if (HQError || HQ.length == 0)
    throw new Error("Error when creating a DriverNote");
  return;
};

const updateHQs = async (data) => {
  const { error: _err } = await supabase
    .from("HQs")
    .update({
      Code: data.Code,
      Description: data.Description,
      HQUser: data.HQUser,
      HQPass: data.HQPass,
      Active: data.Active === true ? 1 : 0,
    })
    .eq("HQID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const createCompanies = async (data) => {
  const { data: Company, error: CompanyError } = await supabase
    .from("Companies")
    .insert({
      Name: data.Name,
      Description: data.Description,
    })
    .select();
  if (CompanyError || Company.length == 0)
    throw new Error("Error when creating a DriverNote");
  return;
};

const updateCompanies = async (data) => {
  const { error: _err } = await supabase
    .from("Companies")
    .update({
      Name: data.Name,
      Description: data.Description,
    })
    .eq("CompanyID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const createDCType = async (data) => {
  const { data: driverCredentialType, error: driverCredentialTypeError } =
    await supabase
      .from("DriverCredentialTypes")
      .insert({
        CompanyID: data.CompanyID,
        Code: data.Code,
        Description: data.Description,
      })
      .select();
  if (driverCredentialTypeError || driverCredentialType.length == 0)
    throw new Error("Error when creating a Driver Credential Type");
  return;
};

const updateDCType = async (data) => {
  const { error: _err } = await supabase
    .from("DriverCredentialTypes")
    .update({
      CompanyID: data.CompanyID,
      Code: data.Code,
      Description: data.Description,
    })
    .eq("DriverCredentialTypeID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const createLocation = async (data) => {
  const { data: location, error: locationError } = await supabase
    .from("Locations")
    .insert({
      CompanyID: data.CompanyID,
      LocationTypeID: data.LocationTypeID,
      Code: data.Code,
      Description: data.Description,
      MinutesFromAirport: data.MinutesFromAirport,
    })
    .select();
  if (locationError || location.length === 0)
    throw new Error("Error when creating a Location");
  return;
};

const updateLocation = async (data) => {
  const { error: _err } = await supabase
    .from("Locations")
    .update({
      CompanyID: data.CompanyID,
      LocationTypeID: data.LocationTypeID,
      Code: data.Code,
      Description: data.Description,
      MinutesFromAirport: data.MinutesFromAirport,
    })
    .eq("LocationID", data.ID)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const searchSchedules = async (_data) => {
  const page = _data.page ?? 1;
  const from = (page - 1) * 10;
  const to = page * 10 - 1;
  
  // Get current manager ID from session for filtering
  const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
  const userRole = sessionStorage.getItem("UserRole");
  
  console.log("🔍 Schedule Search Debug:", {
    userRole,
    currentManagerId,
    locationID: _data.locationID,
    isManager: userRole !== "1"
  });
  
  // Try using ScheduleMixed again but with better error handling
  let query = supabase.from("ScheduleMixed").select(
    `
      ScheduleID,
      ScheduleDate,
      PickupDateTime,
      IsOneOff,
      FlightNo,
      RateOverride,
      AirlineID,
      AirlineLetterCode,
      ManagerID,
      ScheduleStatus: ScheduleStatusID (Code),
      Company: CompanyID (Description),
      TransportationType: TransportationTypeID (Code),
      Driver: DriverID (DriverCode, FirstName, LastName),
      Vehicle: VehicleID (Code, Model),
      Pilots,
      FlightAttendants,
      Passengers,
      PickupLocation: pickuplocationid (Description, LocationID, Code, LocationTypeID),
      DropoffLocation: dropofflocationid (Description, LocationID, Code, LocationTypeID)
    `,
    { count: "exact" }
  );
  // Filter by calendar-day bounds (local time) so the search matches the
  // Reports download and the legacy system.
  if (!!_data.startDate)
    query = query.gte("ScheduleDate", reportDayStart(_data.startDate));
  if (!!_data.endDate)
    query = query.lt("ScheduleDate", reportDayEndExclusive(_data.endDate));
  if (!!_data.airlineID) query = query.eq("AirlineID", _data.airlineID);
  if (!!_data.driverID) query = query.eq("DriverID", _data.driverID);
  if (!!_data.vehicleID) query = query.eq("VehicleID", _data.vehicleID);
  if (!!_data.companyID) query = query.eq("CompanyID", _data.companyID);
  if (!!_data.flightNo) query = query.ilike("FlightNo", `%${_data.flightNo}%`);
  // Apply location filtering first (most important for manager scoping)
  if (!!_data.locationID) {
    console.log(`🏢 Applying location filter for LocationID: ${_data.locationID}`);
    // Filter by pickup or dropoff location using ScheduleMixed field names
    query = query.or(
      `pickuplocationid.eq.${_data.locationID},dropofflocationid.eq.${_data.locationID}`
    );
  }
  if (!!_data.passengers) {
    query = query.eq("Passengers", _data.passengers);
  }
  if (!!_data.airlineCode && _data.airlineCode != "") {
    query = query.ilike("AirlineLetterCode", `%${_data.airlineCode}%`);
  }

  // Explicit manager filter from dropdown — Schedule.ManagerID is the direct link.
  if (!!_data.managerID) {
    query = query.eq("ManagerID", _data.managerID);
  }

  // Filter by manager: show schedules assigned to current manager OR their locations
  if (userRole !== "1" && currentManagerId) {
    console.log(`🧑‍💼 Applying manager filter for UserID: ${currentManagerId}`);

    // Get the manager's locations from UserLocations junction table
    const { data: userLocations } = await supabase
      .from("UserLocations")
      .select("LocationID")
      .eq("UserID", currentManagerId);

    const locationIds = userLocations?.map((ul) => ul.LocationID) || [];

    if (locationIds.length > 0) {
      console.log(`🏢 Manager LocationIDs: ${locationIds.join(", ")}`);

      // Build OR filter for all manager locations. Include ManagerID so
      // ADHOC trips created via TripAdd (which stamps ManagerID = creator
      // but leaves DriverID pointing at the assigned driver) appear on the
      // creator's own report regardless of pickup/dropoff location. Without
      // this, ADHOC trips with non-standard hotels slipped out of the report
      // view but stayed visible in the Excel export.
      const locationFilters = locationIds
        .map((id) => `pickuplocationid.eq.${id},dropofflocationid.eq.${id}`)
        .join(",");
      query = query.or(
        `ManagerID.eq.${currentManagerId},DriverID.eq.${currentManagerId},${locationFilters}`
      );
    } else {
      // Manager has no assigned locations — show trips they created or drive.
      query = query.or(
        `ManagerID.eq.${currentManagerId},DriverID.eq.${currentManagerId}`
      );
    }
  }

  query = query.order("ScheduleDate", { ascending: false });
  query = query.range(from, to);

  const { data, count, error } = await query;
  if (error) {
    console.error("❌ Schedule search error:", error);
    throw new Error(error.toString());
  }
  
  console.log(`✅ Found ${count} schedules`);
  
  // Resolve manager names: prefer Schedule.ManagerID; fall back to location-based
  // lookup (Users.LocationID) for rows with no ManagerID.
  const managerIds = [
    ...new Set(data.map((item) => item.ManagerID).filter(Boolean)),
  ];
  const managerNamesById = {};
  if (managerIds.length > 0) {
    const { data: mgrRows } = await supabase
      .from("Managers")
      .select("UserID, firstname, lastname, email")
      .in("UserID", managerIds);
    (mgrRows || []).forEach((m) => {
      managerNamesById[m.UserID] =
        `${m.firstname || ""} ${m.lastname || ""}`.trim() ||
        m.email ||
        `Manager ${m.UserID}`;
    });
  }

  const locationManagers = {};
  const unassignedLocationIds = [
    ...new Set(
      data
        .filter((item) => !item.ManagerID)
        .flatMap((item) => [
          item.PickupLocation?.LocationID,
          item.DropoffLocation?.LocationID,
        ])
        .filter(Boolean)
    ),
  ];
  if (unassignedLocationIds.length > 0) {
    const { data: usersAtLocs } = await supabase
      .from("Users")
      .select("UserID, LocationID")
      .eq("RoleID", 2)
      .in("LocationID", unassignedLocationIds);
    const fallbackUserIds = [
      ...new Set((usersAtLocs || []).map((u) => u.UserID)),
    ];
    if (fallbackUserIds.length > 0) {
      const { data: mgrRows } = await supabase
        .from("Managers")
        .select("UserID, firstname, lastname, email")
        .in("UserID", fallbackUserIds);
      const nameByUserId = {};
      (mgrRows || []).forEach((m) => {
        nameByUserId[m.UserID] =
          `${m.firstname || ""} ${m.lastname || ""}`.trim() ||
          m.email ||
          `Manager ${m.UserID}`;
      });
      (usersAtLocs || []).forEach((u) => {
        const name = nameByUserId[u.UserID];
        if (!name) return;
        if (!locationManagers[u.LocationID]) locationManagers[u.LocationID] = [];
        if (!locationManagers[u.LocationID].includes(name)) {
          locationManagers[u.LocationID].push(name);
        }
      });
    }
  }

  // Display the time the crew sees, not the raw schedule time. Adjustments
  // only affect hotel→airport (DEPART) rows that have a matching window for
  // the assigned manager; everything else stays untouched.
  const adjustmentsByManager = await loadAdjustmentsByManager();

  // Airline display: "AC - Air Canada" instead of just "AC" — mirrors the
  // Reports page so codes and names read consistently everywhere.
  const airlineIds = [...new Set(data.map((i) => i.AirlineID).filter(Boolean))];
  const airlineNameById = {};
  if (airlineIds.length > 0) {
    const { data: airlineRows } = await supabase
      .from("Airlines")
      .select("AirlineID, AirlineLetterCode, Description")
      .in("AirlineID", airlineIds);
    (airlineRows || []).forEach((a) => {
      const code = a.AirlineLetterCode || "";
      const desc = (a.Description || "").trim();
      airlineNameById[a.AirlineID] =
        desc && desc !== code ? `${code} - ${desc}` : code;
    });
  }

  return {
    list: data.map((_item) => ({
      ScheduleID: _item.ScheduleID,
      ScheduleDate: adjustedScheduleInEastern(_item, adjustmentsByManager),
      Airline: airlineNameById[_item.AirlineID] ?? (_item.AirlineLetterCode ?? ""),
      FlightNo: _item.FlightNo,
      Status: _item.ScheduleStatus?.Code ?? "",
      Company: _item.Company.Description,
      Transport: _item.TransportationType.Code,
      Driver: (() => {
        if (_item.ManagerID && managerNamesById[_item.ManagerID]) {
          return managerNamesById[_item.ManagerID];
        }
        const pickupManagers = locationManagers[_item.PickupLocation?.LocationID] || [];
        const dropoffManagers = locationManagers[_item.DropoffLocation?.LocationID] || [];
        const allManagers = [...new Set([...pickupManagers, ...dropoffManagers])];
        return allManagers.length > 0 ? allManagers.join(", ") : "Unassigned";
      })(),
      Vehicle: _item.Vehicle?.Model ?? "",
      Pilots: 0,
      FA: 0,
      Rate: _item.RateOverride,
      Pass: _item.Passengers ?? 0,
      PU: _item.PickupLocation?.Description ?? "",
      DO: _item.DropoffLocation?.Description ?? "",
    })),
    count,
  };
};

const updateManagerLocation = async (data) => {
  // Add location to manager (additive via UserLocations)
  const { error: ulError } = await supabase
    .from("UserLocations")
    .upsert({
      UserID: data.managerId,
      LocationID: data.locationId,
    }, { onConflict: "UserID,LocationID" });
  if (ulError) throw new Error(ulError.toString());

  // Also update Users.LocationID for backwards compatibility
  const { error: _err } = await supabase
    .from("Users")
    .update({
      "LocationID": data.locationId,
    })
    .eq("UserID", data.managerId)
    .select();
  if (_err) throw new Error(_err.toString());
  return;
};

const emptyHandler = () => {};
const useCreateAirline = (onSuccess, onError) =>
  useMutation({
    mutationFn: createAirline,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateAirline = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateAirline,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateTrip = (onSuccess, onError) =>
  useMutation({
    mutationFn: createTrip,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });
const useUpdateTrip = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateTrip,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateManagerLocation = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateManagerLocation,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useSearchSchedules = (onSuccess, onError) =>
  useMutation({
    mutationFn: searchSchedules,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateManager = (onSuccess, onError) =>
  useMutation({
    mutationFn: createManager,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });
const useUpdateManager = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateManager,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateDriverNote = (onSuccess, onError) =>
  useMutation({
    mutationFn: createDriverNote,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateDriverNote = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateDriverNote,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateHQCompany = (onSuccess, onError) =>
  useMutation({
    mutationFn: createHQCompany,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateHQCompany = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateHQCompany,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateHQs = (onSuccess, onError) =>
  useMutation({
    mutationFn: createHQs,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateHQs = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateHQs,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateCompanies = (onSuccess, onError) =>
  useMutation({
    mutationFn: createCompanies,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateCompanies = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateCompanies,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateDCType = (onSuccess, onError) =>
  useMutation({
    mutationFn: createDCType,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateDCType = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateDCType,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useCreateLocation = (onSuccess, onError) =>
  useMutation({
    mutationFn: createLocation,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

const useUpdateLocation = (onSuccess, onError) =>
  useMutation({
    mutationFn: updateLocation,
    onSuccess: onSuccess ?? emptyHandler,
    onError: onError ?? emptyHandler,
  });

export {
  useCreateAirline,
  useUpdateAirline,
  useCreateTrip,
  useUpdateManagerLocation,
  useSearchSchedules,
  useCreateManager,
  useUpdateManager,
  useCreateDriverNote,
  useUpdateDriverNote,
  useUpdateTrip,
  useCreateHQs,
  useUpdateHQs,
  useCreateCompanies,
  useUpdateCompanies,
  useCreateHQCompany,
  useUpdateHQCompany,
  useCreateDCType,
  useUpdateDCType,
  useCreateLocation,
  useUpdateLocation,
};
